/**
 * Profilbilder (Feedback 11.10.2026) — Personen (BK/PB) und Mitarbeiter.
 *
 * Backend: GET /v2/admin/avatars?user_ids=…&admin_ids=… liefert je ID eine
 * kurzlebige, signierte Bild-URL (oder null). Viele Ansichten laden ihre
 * Personen noch über v1, deshalb hängt das Bild nicht an den Listen-
 * Antworten, sondern wird hier gesammelt nachgeladen:
 *
 *  - Anfragen derselben Render-Runde werden zu EINEM Request gebündelt.
 *  - Ergebnisse (auch „kein Bild") werden gecacht; signierte URLs laufen
 *    nach 60 min ab → Cache-Einträge verfallen nach 45 min.
 */
import { useEffect, useState } from 'react';
import { apiV2 } from './api';

type Kind = 'users' | 'admins';

interface CacheEntry { url: string | null; at: number }

const TTL_MS = 45 * 60 * 1000;
const cache: Record<Kind, Map<number, CacheEntry>> = { users: new Map(), admins: new Map() };
const pending: Record<Kind, Set<number>> = { users: new Set(), admins: new Set() };
const inflight: Record<Kind, Map<number, Promise<string | null>>> = { users: new Map(), admins: new Map() };
const resolvers: Record<Kind, Map<number, Array<(url: string | null) => void>>> = { users: new Map(), admins: new Map() };
const listeners = new Set<() => void>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function fresh(kind: Kind, id: number): CacheEntry | null {
  const hit = cache[kind].get(id);
  return hit && Date.now() - hit.at < TTL_MS ? hit : null;
}

async function flush(): Promise<void> {
  flushTimer = null;
  const users = [...pending.users];
  const admins = [...pending.admins];
  pending.users.clear();
  pending.admins.clear();
  if (users.length === 0 && admins.length === 0) return;

  let data: { users?: Record<string, string | null>; admins?: Record<string, string | null> } = {};
  try {
    const res = await apiV2.get<{ data: typeof data }>('/admin/avatars', {
      params: {
        ...(users.length ? { user_ids: users.join(',') } : {}),
        ...(admins.length ? { admin_ids: admins.join(',') } : {}),
      },
    });
    data = res.data?.data ?? {};
  } catch {
    // Netzwerk-/Rechtefehler: Initialen bleiben, kein erneuter Versuch bis TTL.
  }

  const settle = (kind: Kind, ids: number[]) => {
    for (const id of ids) {
      const url = (data[kind]?.[String(id)] as string | null | undefined) ?? null;
      cache[kind].set(id, { url, at: Date.now() });
      inflight[kind].delete(id);
      (resolvers[kind].get(id) ?? []).forEach(r => r(url));
      resolvers[kind].delete(id);
    }
  };
  settle('users', users);
  settle('admins', admins);
  listeners.forEach(l => l());
}

function request(kind: Kind, id: number): Promise<string | null> {
  const hit = fresh(kind, id);
  if (hit) return Promise.resolve(hit.url);
  const running = inflight[kind].get(id);
  if (running) return running;

  const p = new Promise<string | null>(resolve => {
    const list = resolvers[kind].get(id) ?? [];
    list.push(resolve);
    resolvers[kind].set(id, list);
  });
  inflight[kind].set(id, p);
  pending[kind].add(id);
  if (!flushTimer) flushTimer = setTimeout(() => { void flush(); }, 0);
  return p;
}

function useAvatar(kind: Kind, id: number | null | undefined): string | null {
  const valid = typeof id === 'number' && Number.isFinite(id) && id > 0;
  const [url, setUrl] = useState<string | null>(() => (valid ? fresh(kind, id as number)?.url ?? null : null));
  // Neu laden, wenn sich der Cache ändert (z. B. eigenes Bild hochgeladen).
  const version = useAvatarCacheVersion();
  // Sichtbare Avatare holen sich vor Ablauf der signierten URL (60 min) eine
  // neue — der Cache-Eintrag verfällt nach 45 min, dann wird neu angefragt.
  const [expiryTick, setExpiryTick] = useState(0);

  useEffect(() => {
    if (!valid) { setUrl(null); return; }
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    void request(kind, id as number).then(u => {
      if (!alive) return;
      setUrl(u);
      const entry = cache[kind].get(id as number);
      const remaining = entry ? TTL_MS - (Date.now() - entry.at) : TTL_MS;
      timer = setTimeout(() => setExpiryTick(x => x + 1), Math.max(1000, remaining + 50));
    });
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [kind, id, valid, version, expiryTick]);

  return url;
}

/** Bild einer Person (users.id) — jüngstes Foto der Kategorie „Person". */
export function usePersonAvatar(userId: number | null | undefined): string | null {
  return useAvatar('users', userId);
}

/** Bild eines Mitarbeiters (admins.id). */
export function useAdminAvatar(adminId: number | null | undefined): string | null {
  return useAvatar('admins', adminId);
}

/** Nach Upload/Entfernen des eigenen Bildes den Cache-Eintrag ersetzen. */
export function setAdminAvatarCache(adminId: number, url: string | null): void {
  cache.admins.set(adminId, { url, at: Date.now() });
  listeners.forEach(l => l());
}

/** Eigenes Mitarbeiter-Bild hochladen → neue URL. */
export async function uploadOwnAvatar(file: File): Promise<string | null> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiV2.post<{ data: { url: string | null } }>('/admin/me/avatar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data?.data?.url ?? null;
}

/**
 * Eigenes Mitarbeiter-Bild entfernen → URL, die danach gilt (z. B. das
 * weiterhin vorhandene CRM-1.0-Profilbild) oder null.
 */
export async function deleteOwnAvatar(): Promise<string | null> {
  const res = await apiV2.delete<{ data: { url: string | null } }>('/admin/me/avatar');
  return res.data?.data?.url ?? null;
}

/** Nur für Tests. */
export function __resetAvatarCache(): void {
  (['users', 'admins'] as Kind[]).forEach(k => {
    cache[k].clear(); pending[k].clear(); inflight[k].clear(); resolvers[k].clear();
  });
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
}

/** Re-Render nach Cache-Änderung (z. B. eigenes Bild neu) abonnieren. */
export function useAvatarCacheVersion(): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    const l = () => setV(x => x + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return v;
}

/** „BK-123", „A-123", 123 → 123; sonst null (für Avatar-userId aus Listen-IDs). */
export function numericId(v: string | number | null | undefined): number | null {
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : null;
  if (typeof v !== 'string') return null;
  const m = /(\d+)$/.exec(v);
  return m ? Number(m[1]) : null;
}
